import React, { useState } from "react"
import dayjs from "dayjs"
import { TransferApplication } from "../types"
import { useUpdateTransferApplication } from "../hooks/useUpdateTransferApplication"
import { useCancelTransferApplication } from "../hooks/useCancelTransferApplication"
import { useNotification } from "@smoothincorg/smooth-ui"
import { useCompleteTransferApplication } from "../hooks/useCompleteTransferApplication"

interface TransferApplicationItemDetailsProps {
  application: TransferApplication
  onUpdate: () => void
  onCancel: () => void
}

const TransferApplicationItemDetails: React.FC<TransferApplicationItemDetailsProps> = ({
  application,
  onUpdate,
  onCancel,
}) => {
  const [transferOn, setTransferOn] = useState<string>(application.transferOn)
  const [applyNo, setApplyNo] = useState(application.applyNo)
  const [applyNoError, setApplyNoError] = useState<string | null>(null)
  const [transferOnError, setTransferOnError] = useState<string | null>(null)
  const [isEditClicked, setIsEditClicked] = useState(false)
  const { showSnackbar } = useNotification()
  const { updateTransferApplication } = useUpdateTransferApplication()
  const { cancelTransferApplication } = useCancelTransferApplication()
  const { completeTransferApplication } = useCompleteTransferApplication()

  // applyNoエラーメッセージ生成関数
  const getApplyNoError = (value: string): string | null => {
    if (!value) return "申請IDは必須です"
    if (!/^[0-9]+$/.test(value)) return "申請IDは半角数字で入力してください"
    if (value.length !== 16) return "申請IDは16桁で入力してください"
    return null
  }

  // transferOnエラーメッセージ生成関数
  const getTransferOnError = (value: string): string | null => {
    if (!value) return "振込予定日は必須です"

    return null
  }

  const canSave = applyNo && transferOn && !applyNoError && !transferOnError
  const isEditing =
    application.status === "in_progress" || (application.status === "applied" && isEditClicked)

  const handleEdit = () => {
    setIsEditClicked(true)
  }

  const handleSave = async () => {
    // 保存前最終チェック
    const currentApplyNoError = getApplyNoError(applyNo)
    const currentTransferOnError = getTransferOnError(transferOn)

    if (currentApplyNoError) {
      setApplyNoError(currentApplyNoError)
      showSnackbar(currentApplyNoError, "error")
      return
    }

    if (currentTransferOnError) {
      setTransferOnError(currentTransferOnError)
      showSnackbar(currentTransferOnError, "error")
      return
    }

    try {
      await updateTransferApplication(application.id, {
        applyNo: applyNo,
        transferOn: transferOn,
      })
      setIsEditClicked(false)
      onUpdate()
      showSnackbar("更新しました", "success")
    } catch (error) {
      console.error("更新に失敗しました:", error)
      showSnackbar("更新に失敗しました", "error")
    }
  }

  const handleCancel = async () => {
    const confirmed = window.confirm(
      "この操作は取り消せません。立替送金申請を削除してよろしいですか？"
    )
    if (!confirmed) return

    try {
      await cancelTransferApplication(application.id)
      setIsEditClicked(false)
      onCancel()
      showSnackbar("申請を破棄しました", "success")
    } catch (error) {
      console.error("申請破棄に失敗しました:", error)
      showSnackbar("申請破棄に失敗しました", "error")
    }
  }

  const handleComplete = async () => {
    const confirmed = window.confirm("この申請を送金確認済にします。よろしいですか？")
    if (!confirmed) return

    try {
      await completeTransferApplication(application.id)
      setIsEditClicked(false)
      onUpdate()
      showSnackbar("送金確認済にしました", "success")
    } catch (error: any) {
      showSnackbar(
        `送金確認済への変更に失敗しました：\n ${error.response?.data?.errors?.join("\n")}`,
        "error"
      )
    }
  }

  const renderActionButtons = () => {
    if (application.status === "completed" || application.status === "cancelled") {
      return null // 送金確認済みまたはキャンセルの場合はボタンを表示しない
    }

    if (isEditing) {
      return (
        <>
          <button
            onClick={handleSave}
            disabled={!canSave}
            className={`border border-solid border-medium-gray bg-white px-3 py-4 rounded-md text-lg text-black hover:bg-gray-50 ${
              !canSave ? "bg-gray-300 opacity-50" : ""
            }`}
          >
            保存する
          </button>
          <div className="h-14 w-0 border-l border-solid border-medium-light-gray"></div>
          <button
            onClick={handleCancel}
            className="border border-solid border-medium-gray bg-white px-3 py-4 rounded-md text-lg text-black hover:bg-gray-50"
          >
            申請を破棄する
          </button>
        </>
      )
    }

    if (application.status === "applied" && !isEditClicked) {
      return (
        <>
          <button
            onClick={handleEdit}
            className="border border-solid border-medium-gray bg-white px-3 py-4 text-lg text-black hover:bg-gray-50 rounded-md"
          >
            編集する
          </button>
          <div className="h-14 w-0 border-l border-solid border-medium-light-gray"></div>
          <button
            onClick={handleCancel}
            className="border border-solid border-medium-gray bg-white px-3 py-4 text-lg text-black hover:bg-gray-50 rounded-md"
          >
            申請を破棄する
          </button>
        </>
      )
    }

    if (application.status === "matching_error") {
      return (
        <button
          onClick={handleComplete}
          className="border border-solid border-medium-gray bg-white px-3 py-4 text-lg text-black hover:bg-gray-50 rounded-md"
        >
          送金確認済にする
        </button>
      )
    }
  }

  const handleApplyNoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value

    // 数字のみ許可、16桁まで
    const numericValue = value.replace(/[^0-9]/g, "").slice(0, 16)

    setApplyNo(numericValue)

    const error = getApplyNoError(numericValue)
    setApplyNoError(error)
  }

  const handleTransferOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setTransferOn(value)

    const error = getTransferOnError(value)
    setTransferOnError(error)
  }

  const renderApplicationIdField = () => {
    if (isEditing) {
      return (
        <div className="flex flex-col">
          <div className="border border-medium-light-gray rounded-md bg-white px-3 py-0">
            <input
              type="text"
              name="applyNo"
              value={applyNo || ""}
              onChange={handleApplyNoChange}
              className={`h-12 w-72 rounded-md bg-transparent p-2 text-lg text-black rounded-md ${
                applyNoError ? "border-red-500" : "border-medium-light-gray"
              }`}
              maxLength={16}
            />
          </div>
          {applyNoError && <div className="mt-1 text-sm text-red-500">{applyNoError}</div>}
        </div>
      )
    }

    return <div className="h-8 text-lg font-normal text-black">{applyNo}</div>
  }

  const renderScheduledDateField = () => {
    if (isEditing) {
      return (
        <div className="flex flex-col">
          <div className="border border-medium-light-gray bg-white px-3 py-0">
            <input
              type="date"
              name="transferOn"
              value={transferOn ? transferOn : ""}
              onChange={handleTransferOnChange}
              className={`h-12 w-48 rounded-md bg-transparent p-2 text-lg text-black ${
                transferOnError ? "border-red-500" : "border-medium-light-gray"
              }`}
            />
          </div>
          {transferOnError && <div className="mt-1 text-sm text-red-500">{transferOnError}</div>}
        </div>
      )
    }

    return (
      <div className="h-8 text-lg font-normal text-black">
        {transferOn ? dayjs(transferOn).format("YYYY/MM/DD") : ""}
      </div>
    )
  }

  return (
    <div className="flex items-start justify-end gap-5 py-4">
      <div className="flex flex-col gap-1">
        <div className="h-8 text-lg text-black">
          <span className="font-normal">申請ID</span>
          {isEditing && <span className="ml-1 text-sm text-red-500">*必須</span>}
        </div>
        {renderApplicationIdField()}
      </div>

      <div className="flex flex-col gap-1">
        <div className="h-8 text-lg text-black">
          <span className="font-normal">振込予定日</span>
          {isEditing && <span className="ml-1 text-sm text-red-500">*必須</span>}
        </div>
        {renderScheduledDateField()}
      </div>

      {(application.status === "completed" || application.status === "matching_error") &&
        application.valueDate && (
          <div className="flex flex-col gap-1">
            <div className="text-lg text-black">
              <span className="font-normal">着金日</span>
            </div>
            <div className="text-lg font-normal text-black">
              {application.valueDate && dayjs(application.valueDate).format("YYYY/MM/DD")}
            </div>
          </div>
        )}

      <div className="flex items-center gap-2">{renderActionButtons()}</div>
    </div>
  )
}

export default TransferApplicationItemDetails
